import { Button } from '@neo/primitives/button'
import { Lock, X } from 'lucide-react'
import { useParams } from 'router-v6'
import { useVersionView } from '../../hooks/use-version-view'
import { useVersionsQuery } from '../../queries'
import { useMakeVersionActive } from './use-make-version-active'
import {
  formatChangeSummary,
  formatVersionDateGroup,
  formatVersionTime,
  getVersionEditorLabel,
} from './version-utils'

// Compact mode indicator shown over the canvas while a historical version is open
// (URL `?version`). A read-only chip carries the state; the line names the version,
// when, and who; and the actions (make active / exit) live here so reviewing a version
// isn't a dead end. Fed by the same cached versions query the dropdown uses. (SER-5591)
export const FlowVersionViewingBanner = () => {
  const { id } = useParams()
  const { viewingVersionId, isViewingHistorical, viewVersion } = useVersionView()
  const { data } = useVersionsQuery(id, isViewingHistorical)
  const { requestMakeActive, dialog } = useMakeVersionActive(id)

  if (!isViewingHistorical) return null

  const version = data?.versions?.find((v) => v.id === viewingVersionId)
  // Only show the editor when it's meaningful (Vibe Agent, or a resolved name) — the
  // generic "Manual edit" fallback is noise, so omit it.
  const editorLabel =
    version && (version.is_vibe || version.edited_by) ? getVersionEditorLabel(version) : null
  const summaryText = version ? formatChangeSummary(version.change_summary) : null

  return (
    <>
      <div
        className="absolute left-1/2 top-4 z-30 flex -translate-x-1/2 items-center gap-2.5 rounded-xl border border-st2 bg-background py-1.5 pl-3 pr-2 shadow-md"
        data-testid="version-viewing-banner"
      >
        <span className="inline-flex items-center gap-1.5 rounded-md border border-status-warning/25 bg-status-warning/[0.08] px-2 py-0.5 font-jetbrains text-label uppercase text-status-warning">
          <Lock className="h-3 w-3" />
          Read-only
        </span>

        <span className="text-s-500 text-primary">
          {version ? `Version ${version.version}` : 'Earlier version'}
          {version && (
            <span className="text-s-400 text-secondary ml-1.5">
              <span className="font-jetbrains text-xs-400 tabular-nums">
                {formatVersionDateGroup(version.created_at)},{' '}
                {formatVersionTime(version.created_at)}
              </span>
              {editorLabel ? ` · ${editorLabel}` : ''}
              {summaryText && (
                <span
                  className="inline-block max-w-[28ch] truncate align-bottom"
                  title={summaryText}
                >
                  {` · ${summaryText}`}
                </span>
              )}
            </span>
          )}
        </span>

        {version && (
          <Button
            size="sm"
            onClick={() => requestMakeActive(version)}
            data-testid="button-version-make-active"
          >
            Make active
          </Button>
        )}

        <button
          type="button"
          onClick={() => viewVersion(null)}
          aria-label="Exit version view"
          data-testid="button-exit-version-view"
          className="flex h-7 w-7 items-center justify-center rounded-full text-secondary transition-colors hover:bg-muted hover:text-primary"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {dialog}
    </>
  )
}
